import { useState, useRef } from "react";
import { X, Upload, Download, FileText, CheckCircle2, AlertTriangle, Info, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import api from "../../connector/api";

export default function BulkCheckinModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const downloadTemplate = () => {
    const csvContent = "book_barcode\nBC001\nBC002\nBC003\n";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_checkin_template.csv";
    a.click();

    URL.revokeObjectURL(url);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (loading) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (loading) return;

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith(".csv")) {
        setFile(droppedFile);
        setResult(null);
      } else {
        toast.error("Please provide a valid format (.csv) file.");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) return toast.error("Select a CSV data file first.");

    const reader = new FileReader();
    reader.onload = async (e) => {
      const text = e.target.result;
      const book_barcodes = text
        .split("\n")
        .map((r) => r.trim())
        .filter((r) => r && r.toLowerCase() !== "book_barcode");

      if (book_barcodes.length === 0) {
        toast.error("No valid barcode fields detected inside this sheet.");
        return;
      }

      try {
        setLoading(true);
        const res = await api.post("/loans/bulk_checkin_by_barcode/", { book_barcodes });

        setResult(res.data);
        toast.success(`Successfully balanced ${res.data.checked_in} operations.`);
        onSuccess?.();
      } catch (err) {
        console.error(err);
        toast.error("Bulk database transaction update pipeline failed.");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="fixed inset-0 bg-stone-950/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn">
      <div className="bg-[#1a1613] rounded-2xl shadow-2xl max-w-xl w-full border border-[#2e2720] overflow-hidden flex flex-col max-h-[90vh] transform transition-all font-dynamic-title">
        
        {/* Terminal Header */}
        <div className="px-6 py-4 border-b border-[#2e2720] flex items-center justify-between bg-[#131110]">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-amber-500/10 border border-amber-500/20 rounded-xl text-amber-400">
              <Upload className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-stone-100">Bulk Return Processing</h3>
              <p className="text-xs text-stone-400 mt-0.5">Batch parse returned book transactions via CSV import</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-stone-400 hover:text-stone-200 hover:bg-[#201c17] p-1.5 rounded-xl transition disabled:opacity-30"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body Container */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1 min-h-0">
          
          {/* Informational Context Notice Box */}
          <div className="p-4 bg-[#131110] border border-[#2e2720] rounded-xl flex gap-3 text-xs leading-relaxed text-stone-400">
            <Info className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="font-semibold text-stone-200">Operational Rule Guidelines:</p>
              <p>Upload a structured spreadsheet (.csv format) containing raw barcode targets. Active circulation configurations matching targets update instantly; duplicates or historical logs are skipped automatically.</p>
              <button
                type="button"
                onClick={downloadTemplate}
                className="mt-2 text-xs text-amber-400 hover:text-amber-300 font-bold inline-flex items-center gap-1.5 transition"
              >
                <Download className="h-3.5 w-3.5" /> Download Standard Formatting Template
              </button>
            </div>
          </div>

          {/* Interactive File Drop-Zone Canvas */}
          {!file ? (
            <div
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition flex flex-col items-center justify-center gap-3 group ${
                isDragActive
                  ? "border-amber-500 bg-amber-500/5 text-amber-400"
                  : "border-[#2e2720] bg-[#131110] hover:border-[#3e352b] text-stone-400"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileChange}
              />
              <div className="w-12 h-12 rounded-2xl bg-[#1a1613] border border-[#2e2720] text-stone-500 flex items-center justify-center group-hover:scale-105 group-hover:text-amber-400 group-hover:border-amber-500/30 group-hover:bg-amber-500/10 transition duration-200">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-bold text-stone-200">Drop your CSV data sheet here</p>
                <p className="text-xs text-stone-500 mt-1">or click to browse local storage directories</p>
              </div>
            </div>
          ) : (
            /* Selected File Verification Card */
            <div className="p-4 border border-[#2e2720] rounded-xl bg-[#131110] flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center shrink-0">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-stone-200 truncate">{file.name}</p>
                  <p className="text-xs text-stone-400 font-mono">{(file.size / 1024).toFixed(1)} KB • Ready for batch run</p>
                </div>
              </div>
              <button
                type="button"
                disabled={loading}
                onClick={() => setFile(null)}
                className="p-2 text-stone-400 hover:text-rose-400 hover:bg-rose-500/10 rounded-xl transition disabled:opacity-30"
                title="Remove selected dataset file"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Execution Metrics Logging Panel */}
          {result && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                  <div>
                    <p className="text-[10px] font-bold text-emerald-400 uppercase tracking-wide">Processed Successfully</p>
                    <p className="text-lg font-black text-stone-100 leading-tight">
                      {result.checked_in} <span className="text-xs text-stone-400 font-normal">items</span>
                    </p>
                  </div>
                </div>
                <div className="p-3 bg-[#131110] border border-[#2e2720] rounded-xl flex items-center gap-3">
                  <div className="w-5 h-5 rounded bg-[#1a1613] border border-[#2e2720] text-stone-400 text-xs font-bold flex items-center justify-center font-mono">
                    ∑
                  </div>
                  <div>
                    <p className="text-[10px] font-bold text-stone-400 uppercase tracking-wide">Total Identified Rows</p>
                    <p className="text-lg font-black text-stone-100 leading-tight">
                      {result.total || result.requested} <span className="text-xs text-stone-400 font-normal">lines</span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Exception Logs */}
              {result.errors?.length > 0 && (
                <div className="border border-rose-500/20 rounded-xl bg-rose-500/5 overflow-hidden">
                  <div className="px-4 py-2.5 bg-rose-500/10 border-b border-rose-500/20 flex items-center gap-2 text-rose-300 text-xs font-bold">
                    <AlertTriangle className="h-4 w-4 text-rose-400" />
                    <span>Exceptions Discovered ({result.errors.length})</span>
                  </div>
                  <div className="p-3 max-h-36 overflow-y-auto divide-y divide-rose-500/10 font-mono text-[11px] text-rose-300 space-y-1.5">
                    {result.errors.map((err, i) => (
                      <div key={i} className="pt-1.5 first:pt-0 flex items-start gap-2 justify-between">
                        <span className="font-semibold bg-rose-500/20 px-1.5 py-0.5 rounded text-rose-200 border border-rose-500/30">
                          {err.book_barcode || "Unknown Barcode"}
                        </span>
                        <span className="text-right text-stone-400 font-sans font-medium">{err.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Action Control Footer */}
        <div className="px-6 py-4 bg-[#131110] flex items-center justify-end gap-2 border-t border-[#2e2720]">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border border-[#2e2720] font-mono bg-[#131110] text-stone-300 hover:text-stone-100 hover:bg-[#201c17] font-semibold text-xs rounded-xl transition disabled:opacity-50"
          >
            Close
          </button>
          
          <button
            type="button"
            onClick={handleUpload}
            disabled={loading || !file}
            className="inline-flex items-center font-mono gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-stone-950 font-bold text-xs rounded-xl shadow transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>{loading ? "Processing Sync..." : "Check-In"}</span>
            <Upload className="h-4 w-4" />
          </button>
        </div>

      </div>
    </div>
  );
}